import React from 'react';
import { View, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import DisplayIndiGrainResultsTable from '../../components/DisplayIndiGrainResultsTable';
import { JobStatus } from '../../models';
import { IStore } from '../../redux/reducers';
import { containerStyle } from 'src/styles/containerStyle';
import { Box, Text, useColorModeValue } from 'native-base';
import { getText } from 'src/utils/i18n';
import { useIsFocused } from '@react-navigation/native';
import { isAveragingJob } from 'src/utils/jobUtils';
import * as GrainAPI from 'src/api/grain';
import {
    getRejectSelectionSessionKey,
} from 'src/utils/rejectConfig';
import { isElectron } from 'src/utils/miscUtils';
import {
    IRejectConfigElectronApi,
    getRejectConfigStateKeys,
    loadCurrentGrainRejectionMapping,
    loadCurrentProfileRejectConfig,
    resolveEffectiveRejectSelectionConfig,
} from 'src/utils/profileRejectConfig';

export default function IndiGrainResultsPage() {
    const job = useSelector((store: IStore) => store.job.jobStatus.job);
    const jobStatus = useSelector((store: IStore) => store.job.currentJobStatus);
    const userSettings = useSelector((store: IStore) => store.user.userSettings);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);

    const productType = useSelector((store: IStore) => store.job.jobStatus.job?.productName)
        || "GRAIN_ANALYSIS";
    const isAveraged = isAveragingJob(job);
    const { mappingStateKey, profileStateKey } = getRejectConfigStateKeys({
        jobId: job?.jobId,
        grainId: job?.grainId,
        profileId: job?.profileId,
    });
    const [grainRejectionMappingState, setGrainRejectionMappingState] = React.useState<{
        grainId: string;
        mapping?: Record<string, string>;
    }>({ grainId: '' });
    const [profileRejectConfigState, setProfileRejectConfigState] = React.useState<{
        identity: string;
        loaded: boolean;
        config?: {
            particleDistributionRejectKeys?: string[];
            rejectedGrainsRejectKeys?: string[];
        };
    }>({ identity: '', loaded: false });
    const grainRejectionMapping = grainRejectionMappingState.grainId === mappingStateKey
        ? grainRejectionMappingState.mapping
        : undefined;
    const profileRejectConfig = profileRejectConfigState.identity === profileStateKey
        ? profileRejectConfigState.config
        : undefined;
    const isProfileRejectConfigLoading = Boolean(
        job?.grainId
        && job?.profileId
        && !(profileRejectConfigState.identity === profileStateKey && profileRejectConfigState.loaded)
    );
    const sessionKey = React.useMemo(
        () => getRejectSelectionSessionKey(job?.grainId, job?.profileId),
        [job?.grainId, job?.profileId]
    );
    const storedRejectSelectionConfig = userSettings?.[sessionKey];
    const jobRejectionMappings = React.useMemo(() => {
        const mappings = job?.additionalAttributes?.rejectionMappings;
        return mappings && Object.keys(mappings).length > 0 ? mappings : undefined;
    }, [job?.additionalAttributes?.rejectionMappings]);

    React.useEffect(() => {
        let cancelled = false;

        const loadMapping = async () => {
            setGrainRejectionMappingState({ grainId: mappingStateKey });
            if (jobRejectionMappings) {
                setGrainRejectionMappingState({ grainId: mappingStateKey, mapping: jobRejectionMappings });
                return;
            }

            if (!job?.grainId) {
                setGrainRejectionMappingState({ grainId: mappingStateKey });
                return;
            }

            const electron = isElectron() && typeof window !== 'undefined'
                ? (window as Window & { electron?: IRejectConfigElectronApi }).electron
                : undefined;
            const mapping = await loadCurrentGrainRejectionMapping({
                grainId: String(job.grainId),
                getLocalGrains: electron?.getGrains
                    ? () => electron.getGrains()
                    : undefined,
                getCloudMapping: isOnline !== false
                    ? GrainAPI.getRejectionMapping
                    : undefined,
            });
            if (!cancelled) {
                setGrainRejectionMappingState({ grainId: mappingStateKey, mapping });
            }
        };

        loadMapping();
        return () => {
            cancelled = true;
        };
    }, [mappingStateKey, isOnline, job?.grainId, jobRejectionMappings]);

    React.useEffect(() => {
        let cancelled = false;

        const loadProfileRejectConfig = async () => {
            setProfileRejectConfigState({ identity: profileStateKey, loaded: false });
            if (!job?.grainId || !job?.profileId) {
                setProfileRejectConfigState({ identity: profileStateKey, loaded: true });
                return;
            }

            const electron = isElectron() && typeof window !== 'undefined'
                ? (window as Window & { electron?: IRejectConfigElectronApi }).electron
                : undefined;
            const matchedProfile = await loadCurrentProfileRejectConfig({
                grainId: String(job.grainId),
                profileId: String(job.profileId),
                getLocalProfiles: electron?.getGrainProfiles
                    ? (args) => electron.getGrainProfiles(args)
                    : undefined,
                getCloudProfiles: isOnline !== false
                    ? GrainAPI.getGrainProfiles
                    : undefined,
            });
            if (!cancelled) {
                setProfileRejectConfigState({
                    identity: profileStateKey,
                    loaded: true,
                    config: matchedProfile ? {
                        particleDistributionRejectKeys: matchedProfile.particleDistributionRejectKeys,
                        rejectedGrainsRejectKeys: matchedProfile.rejectedGrainsRejectKeys,
                    } : undefined,
                });
            }
        };

        loadProfileRejectConfig();
        return () => {
            cancelled = true;
        };
    }, [isOnline, job?.grainId, job?.profileId, profileStateKey]);

    const rejectionCalculationConfig = React.useMemo(() => {
        if (!job?.grainId || !job?.profileId) {
            return undefined;
        }
        if (isProfileRejectConfigLoading) {
            return undefined;
        }
        return resolveEffectiveRejectSelectionConfig({
            grainId: job.grainId,
            profileId: job.profileId,
            rejectionMapping: grainRejectionMapping,
            profile: profileRejectConfig,
            storedConfig: storedRejectSelectionConfig,
            jobConfig: job.results?.rejectionCalculationConfig,
        });
    }, [grainRejectionMapping, isProfileRejectConfigLoading, job?.grainId, job?.profileId, job?.results?.rejectionCalculationConfig, profileRejectConfig, storedRejectSelectionConfig]);

    const isFocused = useIsFocused();
    const textColor = useColorModeValue('coolGray.800', 'coolGray.100');

    if (!isFocused) {
        return <Box variant={"main"} style={containerStyle.mainContainer}>
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Text color={textColor} style={{ padding: 12, textAlign: 'center' }}>{getText('loading')}</Text>
            </View>
        </Box>
    }

    // Show message if this is an averaging job
    if (isAveraged) {
        return (
            <Box variant={"main"} style={containerStyle.mainContainer}>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Text color={textColor} style={{ padding: 12, textAlign: 'center' }}>
                        {getText('individualGrainAveragedUnavailable')}
                    </Text>
                </View>
            </Box>
        );
    }

    if (isProfileRejectConfigLoading) {
        return <Box variant={"main"} style={containerStyle.mainContainer}>
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Text color={textColor} style={{ padding: 12, textAlign: 'center' }}>{getText('loading')}</Text>
            </View>
        </Box>
    }

    return (
        <Box variant={"main"} style={containerStyle.mainContainer}>
            {(jobStatus == JobStatus.JOB_COMPLETED
                && productType !== "L10_GRAIN_ANALYSIS"
                && productType !== "MANUAL_GRAIN_ANALYSIS"
                && job) ? (<>
                    <ScrollView>
                        <DisplayIndiGrainResultsTable
                            jobId={job.jobId}
                            numItemsPerPage={10}
                            productType={productType}
                            csvFileUrl={job.results?.indi_grain_csv || ''}
                            rejectionCalculationConfig={rejectionCalculationConfig}
                            rejectionMapping={grainRejectionMapping} />
                    </ScrollView>
                </>) : ((productType !== "L10_GRAIN_ANALYSIS"
                    && productType !== "MANUAL_GRAIN_ANALYSIS"
                ) ? (<>
                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Text color={textColor} style={{ padding: 12, textAlign: 'center' }}>{getText('selectImageTabNotice')}</Text>
                    </View>
                </>) : (
                    <>
                        <View
                            style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                            <Text
                                color={textColor}
                                style={{ padding: 12, textAlign: 'center' }}>{getText('noResultsFound')}
                            </Text>
                        </View>
                    </>
                )
            )}
        </Box>
    );

}
